import { IUser } from "../contexts/types";
import { useMemo } from "react";
import { apiCache } from "./cacheManager";
import {
  CAPABILITIES,
  RbacLevel,
  CapabilityKey,
  isGrantLevel,
  levelRank,
} from "./rbacCatalog";

const UNGOVERNED_ROLES = new Set(["OWNER", "DEFAULT"]);

export class PermissionError extends Error {
  public readonly requiredFeature?: string;
  public readonly currentPlan?: string;
  public readonly requiredPlan?: string;

  constructor(
    message: string,
    requiredFeature?: string,
    currentPlan?: string,
    requiredPlan?: string
  ) {
    super(message);
    this.name = "PermissionError";
    this.requiredFeature = requiredFeature;
    this.currentPlan = currentPlan;
    this.requiredPlan = requiredPlan;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, PermissionError);
    }
  }
}

export class SubscriptionError extends Error {
  public readonly subscriptionStatus?: string;
  public readonly expiryDate?: string;
  public readonly currentPlan?: string;

  constructor(
    message: string,
    subscriptionStatus?: string,
    expiryDate?: string,
    currentPlan?: string
  ) {
    super(message);
    this.name = "SubscriptionError";
    this.subscriptionStatus = subscriptionStatus;
    this.expiryDate = expiryDate;
    this.currentPlan = currentPlan;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SubscriptionError);
    }
  }
}

export enum PlanTier {
  FREE = "free",
  PREMIUM = "premium",
  LARGE_BUSINESS = "large_business",
  ENTERPRISE = "enterprise",
  BUSINESS_PARTNER = "business_partner",
  PROMO_ENTERPRISE_TRIAL = "promo_enterprise_trial",
}

export const PLAN_MAPPINGS = {
  [PlanTier.FREE]: ["UMD_FREE", "UMD_FREE_MONTH", "UMD_FREE_ANNUAL"],
  [PlanTier.PREMIUM]: [
    "UMD_PREMIUM_BUSINESS",
    "UMD_PREMIUM_BUSINESS_MONTH",
    "UMD_PREMIUM_BUSINESS_ANNUAL",
    "UMD_PREMIUM_BUSINESS_ANNUAL_WITH_TRIAL",
    "UMD_SMALL_BUSINESS",
    "UMD_SMALL_BUSINESS_MONTH",
    "UMD_SMALL_BUSINESS_ANNUAL",
    "SUMOA_T9IZI415ZG",
    "SUMOA_9X8ZNA5QAO",
    "SUMOA_HOYZZNJVYT",
    "SUMOA_EUEIVLUAUW",
    "SUMOM_8PS8HPOLTF",
    "SUMOM_MHMYDHE9NH",
    // Switzerland Edition (CHF)
    "SUMOM_USXDYFUDSY",
    "SUMOA_HVDYRRYCHY",
    "SUMBU_EECBUSTIER1",
  ],
  [PlanTier.ENTERPRISE]: [
    "UMD_ENTREPRISE_BUSINESS",
    "UMD_ENTREPRISE_BUSINESS_MONTH",
    "UMD_ENTREPRISE_BUSINESS_ANNUAL",
    "UMD_ENTREPRISE_BUSINESS_ANNUAL_WITH_TRIAL",
    "SUMOA_YNL4PD6BOM",
    "SUMOA_GSW9Q445J1",
    "SUMOA_BOAXCZVLIQ",
    "SUMOA_VJTRNHTBIB",
    "SUMOM_GYASCZ0RSW",
    "SUMOM_JVXKQNIFEN",
    // Switzerland Edition (CHF)
    "SUMOM_PAISDXBDUS",
    "SUMOA_OGIDHUNOFG",
    "SUMBU_EECBUSTIER2",
  ],
  [PlanTier.LARGE_BUSINESS]: [
    "UMD_LARGE_BUSINESS",
    "UMD_LARGE_BUSINESS_MONTH",
    "UMD_LARGE_BUSINESS_ANNUAL",
    "UMD_LARGE_BUSINESS_ANNUAL_WITH_TRIAL",
    "SUMOA_7AWE1A948A",
    "SUMOA_ZN8D246RA5",
    "SUMOA_ZEDIVSBYFC",
    "SUMOA_HZVGJGKYLM",
    "SUMOM_A8NQTU2UIB",
    "SUMOM_7YV3KX83JA",
    "SUMBU_EECBUSTIER3",
    "SUMIN_STAFFTIER3",
  ],
  [PlanTier.BUSINESS_PARTNER]: ["SUM_BUSINESS_PARTNER"],
  [PlanTier.PROMO_ENTERPRISE_TRIAL]: ["UMD_PROMO_ENTERPRISE_TRIAL_14_DAYS"],
} as const;

const PARTNER_PLAN_CODES: readonly string[] = [
  "SUM_BUSINESS_PARTNER",
  "SUMBU_EECBUSTIER1",
  "SUMBU_EECBUSTIER2",
  "SUMBU_EECBUSTIER3",
];

const ALL_PLAN_TIERS = [
  PlanTier.FREE,
  PlanTier.PREMIUM,
  PlanTier.ENTERPRISE,
  PlanTier.LARGE_BUSINESS,
  PlanTier.BUSINESS_PARTNER,
] as const;

const PAID_PLAN_TIERS = [
  PlanTier.PREMIUM,
  PlanTier.ENTERPRISE,
  PlanTier.LARGE_BUSINESS,
] as const;

const ENTERPRISE_AND_ABOVE = [
  PlanTier.ENTERPRISE,
  PlanTier.LARGE_BUSINESS,
] as const;

const LARGE_BUSINESS_ONLY = [PlanTier.LARGE_BUSINESS] as const;

const PARTNER_ONLY = [PlanTier.BUSINESS_PARTNER] as const;

export const FEATURE_PERMISSIONS = {
  USER_MANAGEMENT: ALL_PLAN_TIERS,
  GROUP_MANAGEMENT: ALL_PLAN_TIERS,
  PROFILE_MANAGEMENT: ALL_PLAN_TIERS,

  USER_EVENTS: ALL_PLAN_TIERS,
  USER_NOTES: ALL_PLAN_TIERS,
  USER_DATA_SPACES: ALL_PLAN_TIERS,
  USER_DATA_SPACES_EXTEND_5: PAID_PLAN_TIERS,
  USER_DATA_SPACES_EXTEND_10: ENTERPRISE_AND_ABOVE,
  USER_DATA_SPACES_EXTEND_UNLIMITED: LARGE_BUSINESS_ONLY,

  SEND_MESSAGES: PAID_PLAN_TIERS,
  BULK_MESSAGING: PAID_PLAN_TIERS,
  SEND_SMS: ENTERPRISE_AND_ABOVE,
  SEND_WHATSAPP: ENTERPRISE_AND_ABOVE,
  AI_QUALITY_CONTROL: PAID_PLAN_TIERS,
  PHONE_NUMBER_VALIDATION: PAID_PLAN_TIERS,

  LOYALTY_PROGRAM_ACCESS: ALL_PLAN_TIERS,
  LOYALTY_POINTS_BASIC: ALL_PLAN_TIERS,
  GOOGLE_REVIEWS: PAID_PLAN_TIERS,
  GOOGLE_APPLE_WALLET: PAID_PLAN_TIERS,
  LOYALTY_CALLBOT: ENTERPRISE_AND_ABOVE,
  LOYALTY_PROGRAM_ALL_TYPE: PAID_PLAN_TIERS,
  LOYALTY_GAMING: PAID_PLAN_TIERS,
  AUTO_PILOT: ENTERPRISE_AND_ABOVE,

  MANUAL_USER_VIA_FORMS: ALL_PLAN_TIERS,
  ADD_USER_MANUALLY: ALL_PLAN_TIERS,
  BULK_USER_IMPORT: PAID_PLAN_TIERS,

  SERVICE_AVATAR_UPDATE: ALL_PLAN_TIERS,
  PROFILE_CUSTOMIZATION: ALL_PLAN_TIERS,

  ENTITY_PREFERENCES: ALL_PLAN_TIERS,
  ENTITY_STATUS_MANAGEMENT: PAID_PLAN_TIERS,
  ROLE_TYPE_EDITOR: ALL_PLAN_TIERS,

  LOGIN_TEMPLATES_BASIC: PAID_PLAN_TIERS,
  CUSTOM_CALLBACK_URL: PAID_PLAN_TIERS,

  BUSINESS_CARDS_BASIC: ALL_PLAN_TIERS,
  BUSINESS_CARDS_REGISTER_FEATURE: PAID_PLAN_TIERS,
  BUSINESS_CARDS_SAVE_CONTACT_BUTTON: ENTERPRISE_AND_ABOVE,
  BUSINESS_CARDS_THEMES: PAID_PLAN_TIERS,
  BUSINESS_CARDS_UPLOAD_FILES: ENTERPRISE_AND_ABOVE,

  CUSTOM_LINKS: PAID_PLAN_TIERS,

  ADVANCED_SEARCH: ENTERPRISE_AND_ABOVE,

  AI_ENGINE: ENTERPRISE_AND_ABOVE,

  SIGNUP_FORM: ALL_PLAN_TIERS,
  SIGNUP_FORM_EXTENDED_LIMIT: PAID_PLAN_TIERS,
  SIGNUP_FORM_ENTERPRISE_LIMIT: ENTERPRISE_AND_ABOVE,
  SIGNUP_FORM_UNLIMITED: LARGE_BUSINESS_ONLY,
  SIGNUP_FORM_REDIRECT_URL: PAID_PLAN_TIERS,
  CUSTOM_REGISTRATION_AUTOMATED_EMAIL: PAID_PLAN_TIERS,
  CUSTOM_REGISTRATION_EMAIL_ATTACHMENTS: ENTERPRISE_AND_ABOVE,
  KEEP_UNVERIFIED_USERS_POLICY: ENTERPRISE_AND_ABOVE,
  ROLE_MANAGEMENT_ACCESS: PAID_PLAN_TIERS,

  EXPORT_DATA: ENTERPRISE_AND_ABOVE,
  API_ACCESS: LARGE_BUSINESS_ONLY,
  SWAGGER_DOCUMENTATION: LARGE_BUSINESS_ONLY,
  WEBHOOK_EVENTS: ENTERPRISE_AND_ABOVE,
  SYSTEM_INTEGRATION: LARGE_BUSINESS_ONLY,
  CUSTOM_DEVELOPMENT: LARGE_BUSINESS_ONLY,

  ROI_GUARANTEE: PAID_PLAN_TIERS,
  SETUP_PACK: ENTERPRISE_AND_ABOVE,
  SUPPORT_ACCESS: ALL_PLAN_TIERS,
  BUSINESS_PARTNER_PANEL: PARTNER_ONLY,
  DEDICATED_ACCOUNT_MANAGER: LARGE_BUSINESS_ONLY,
  EXPERT_SUPPORT_12H: LARGE_BUSINESS_ONLY,

  CUSTOM_REPORTS: LARGE_BUSINESS_ONLY,
  RATE_LIMIT_PREMIUM: PAID_PLAN_TIERS,
  RATE_LIMIT_ENTERPRISE: ENTERPRISE_AND_ABOVE,
  RATE_LIMIT_LARGE_BUSINESS: LARGE_BUSINESS_ONLY,
  USER_EVENT_ACCESS: ALL_PLAN_TIERS,
  BUSINESS_CARDS_STATS_VIEW: PAID_PLAN_TIERS,
  BUSINESS_CARDS_ADVANCED_STATS: ENTERPRISE_AND_ABOVE,

  PAYMENT_REQUEST_CREATION: ENTERPRISE_AND_ABOVE,

  FREE_EVENTS: PAID_PLAN_TIERS,
  PAID_EVENTS: ENTERPRISE_AND_ABOVE,

  MOBILE_APP_ACCESS: PAID_PLAN_TIERS,
  MOBILE_APP_TEAM_ACCESS_1: PAID_PLAN_TIERS,
  MOBILE_APP_TEAM_ACCESS_MULTI: ENTERPRISE_AND_ABOVE,

  TEAM_MEMBER_ACCESS_1: PAID_PLAN_TIERS,

  ESIGNATURE_ACCESS: ENTERPRISE_AND_ABOVE,

  IMPORT_FROM_DOCUMENT: ENTERPRISE_AND_ABOVE,

  LEADS_MANAGEMENT: ALL_PLAN_TIERS,

  TODOS_MANAGEMENT: ALL_PLAN_TIERS,

  AGENDA: ALL_PLAN_TIERS,
} as const;

export const FEATURE_DISPLAY_NAMES = {
  USER_MANAGEMENT: "Manage Users",
  GROUP_MANAGEMENT: "Manage Groups",
  PROFILE_MANAGEMENT: "Manage Profiles",

  USER_EVENTS: "Track User Activity",
  USER_NOTES: "Add Notes to Users",
  USER_DATA_SPACES: "Organize user information and files safely in one place",
  USER_DATA_SPACES_EXTEND_5:
    "Up to 5 folders, 5 items per folder, and 5 files per item",
  USER_DATA_SPACES_EXTEND_10:
    "Up to 10 folders, 10 items per folder, and 10 files per item",
  USER_DATA_SPACES_EXTEND_UNLIMITED: "Unlimited folders, items, and files",

  SEND_MESSAGES: "Send Messages",
  BULK_MESSAGING: "Send Bulk Messages to Groups",
  SEND_SMS: "One-on-one customer messaging via SMS",
  SEND_WHATSAPP: "Rich customer messaging via WhatsApp",
  AI_QUALITY_CONTROL: "AI Message Quality Check",
  PHONE_NUMBER_VALIDATION: "Phone Number Verification",

  LOYALTY_PROGRAM_ACCESS: "Access Loyalty Programs",
  GOOGLE_REVIEWS: "Collect Google Reviews",
  GOOGLE_APPLE_WALLET: "Google & Apple Wallet Integration",
  LOYALTY_CALLBOT: "24/7 Automated CallBot",
  LOYALTY_POINTS_BASIC: "Basic Points Campaign",
  LOYALTY_PROGRAM_ALL_TYPE: "All Loyalty Programs Types",
  LOYALTY_GAMING: "Loyalty Program Games (Spinner & Scratch Card)",
  AUTO_PILOT:
    "Loyalty Programs in Auto-Pilot mode (Smart re-engagement campaigns with AI)",

  MANUAL_USER_VIA_FORMS: "Register Users via Forms",
  ADD_USER_MANUALLY: "Add Users Manually",
  BULK_USER_IMPORT: "Import Users in Bulk",

  SERVICE_AVATAR_UPDATE: "Update Service Avatar",
  PROFILE_CUSTOMIZATION: "Customize Profiles",

  ENTITY_PREFERENCES: "Manage Entity Settings",
  ENTITY_STATUS_MANAGEMENT: "Control Entity Status",
  ROLE_TYPE_EDITOR: "Edit User Roles",

  LOGIN_TEMPLATES_BASIC: "Use Login page",
  CUSTOM_CALLBACK_URL: "Custom Redirect URLs",

  BUSINESS_CARDS_BASIC: "Create Digital Business Cards",
  BUSINESS_CARDS_REGISTER_FEATURE: "Register Users via Business Cards",
  BUSINESS_CARDS_THEMES: "All Business Card Themes",
  BUSINESS_CARDS_UPLOAD_FILES: "Upload Files to Business Cards",
  BUSINESS_CARDS_SAVE_CONTACT_BUTTON: "Save Contact (vCard)",

  CUSTOM_LINKS: "Add Custom Links",

  ADVANCED_SEARCH: "Advanced User & Payment Search",
  AI_ENGINE: "Ask your data in natural language & search inside your files",

  SIGNUP_FORM: "1 Custom Sign-up Form",
  SIGNUP_FORM_EXTENDED_LIMIT: "Up to 2 Sign-up Forms · 10 questions each",
  SIGNUP_FORM_ENTERPRISE_LIMIT:
    "Up to 5 Sign-up Forms · 15 questions each + file upload",
  SIGNUP_FORM_UNLIMITED: "Unlimited Sign-up Forms",
  SIGNUP_FORM_REDIRECT_URL: "Post-Registration Redirect URL",
  CUSTOM_REGISTRATION_AUTOMATED_EMAIL: "Automated Registration Email",
  CUSTOM_REGISTRATION_EMAIL_ATTACHMENTS: "Registration Email Attachments",
  KEEP_UNVERIFIED_USERS_POLICY: "Keep Unverified Users",
  ROLE_MANAGEMENT_ACCESS: "Roles & Permissions",

  EXPORT_DATA: "Export User Data",
  API_ACCESS: "Access API",
  SWAGGER_DOCUMENTATION: "View API Documentation",
  WEBHOOK_EVENTS: "Receive Webhook Events",
  SYSTEM_INTEGRATION: "Integration with Your Existing Systems",
  CUSTOM_DEVELOPMENT: "Development of Custom Features",

  ROI_GUARANTEE: "ROI Guarantee (Annual plan only)",
  SETUP_PACK: "Expert setup offered 🎁",
  BUSINESS_PARTNER_PANEL: "Access Partner Dashboard",
  SUPPORT_ACCESS: "Access Support",
  DEDICATED_ACCOUNT_MANAGER: "Dedicated Account Manager",
  EXPERT_SUPPORT_12H: "Expert Support 12h",

  CUSTOM_REPORTS: "Custom User & Account Activity Reports",
  RATE_LIMIT_PREMIUM: "Premium Rate Limit",
  RATE_LIMIT_ENTERPRISE: "Enterprise Rate Limit",
  RATE_LIMIT_LARGE_BUSINESS: "Large Business Rate Limit",
  USER_EVENT_ACCESS: "Track User Events",
  BUSINESS_CARDS_STATS_VIEW: "Stats about business cards view",
  BUSINESS_CARDS_ADVANCED_STATS: "Advanced stats about business cards",

  PAYMENT_REQUEST_CREATION: "Payment links and recurring subscriptions",

  FREE_EVENTS: "Create Free Events",
  PAID_EVENTS: "Create Paid Events",

  MOBILE_APP_ACCESS: "Loyalty Programs Mobile App",
  MOBILE_APP_TEAM_ACCESS_1: "Loyalty Programs Access for 1 Team Member",
  MOBILE_APP_TEAM_ACCESS_MULTI: "Loyalty Programs Access for All Team",

  TEAM_MEMBER_ACCESS_1: "Unlimited Team Members Access",

  ESIGNATURE_ACCESS: "E-Signature Documents",

  IMPORT_FROM_DOCUMENT: "Import Data from Documents",

  LEADS_MANAGEMENT: "Prospects",

  TODOS_MANAGEMENT: "Task boards",

  AGENDA: "Agenda and appointments",
} as const;

export const PLAN_HIGHLIGHT_FEATURES = {
  [PlanTier.FREE]: [
    "USER_MANAGEMENT",
    "GROUP_MANAGEMENT",
    "USER_EVENTS",
    "LOYALTY_POINTS_BASIC",
    "BUSINESS_CARDS_BASIC",
    "SIGNUP_FORM",
    "USER_DATA_SPACES",
    "AGENDA",
  ],
  [PlanTier.PREMIUM]: [
    "SEND_MESSAGES",
    "BULK_MESSAGING",
    "AI_QUALITY_CONTROL",
    "PHONE_NUMBER_VALIDATION",
    "LOYALTY_PROGRAM_ALL_TYPE",
    "LOYALTY_GAMING",
    "GOOGLE_REVIEWS",
    "GOOGLE_APPLE_WALLET",
    "MOBILE_APP_TEAM_ACCESS_1",
    "TEAM_MEMBER_ACCESS_1",
    "BUSINESS_CARDS_THEMES",
    "BUSINESS_CARDS_REGISTER_FEATURE",
    "BUSINESS_CARDS_STATS_VIEW",
    "FREE_EVENTS",
    "BULK_USER_IMPORT",
    "SIGNUP_FORM_EXTENDED_LIMIT",
    "USER_DATA_SPACES_EXTEND_5",
    "ROI_GUARANTEE",
    "CUSTOM_CALLBACK_URL",
  ],
  [PlanTier.ENTERPRISE]: [
    "SEND_SMS",
    "SEND_WHATSAPP",
    "AI_ENGINE",
    "LOYALTY_CALLBOT",
    "AUTO_PILOT",
    "MOBILE_APP_TEAM_ACCESS_MULTI",
    "BUSINESS_CARDS_UPLOAD_FILES",
    "BUSINESS_CARDS_SAVE_CONTACT_BUTTON",
    "BUSINESS_CARDS_ADVANCED_STATS",
    "PAID_EVENTS",
    "PAYMENT_REQUEST_CREATION",
    "ESIGNATURE_ACCESS",
    "IMPORT_FROM_DOCUMENT",
    "ADVANCED_SEARCH",
    "SIGNUP_FORM_ENTERPRISE_LIMIT",
    "USER_DATA_SPACES_EXTEND_10",
    "EXPORT_DATA",
    "CUSTOM_REPORTS",
    "WEBHOOK_EVENTS",
  ],
  [PlanTier.LARGE_BUSINESS]: [
    "USER_DATA_SPACES_EXTEND_UNLIMITED",
    "SIGNUP_FORM_UNLIMITED",
    "CUSTOM_REPORTS",
    "API_ACCESS",
    "SYSTEM_INTEGRATION",
    "CUSTOM_DEVELOPMENT",
    "DEDICATED_ACCOUNT_MANAGER",
    "EXPERT_SUPPORT_12H",
  ],
  [PlanTier.BUSINESS_PARTNER]: ["BUSINESS_PARTNER_PANEL"],
  [PlanTier.PROMO_ENTERPRISE_TRIAL]: [
    "LOYALTY_CALLBOT",
    "MOBILE_APP_TEAM_ACCESS_MULTI",
    "BUSINESS_CARDS_UPLOAD_FILES",
    "BUSINESS_CARDS_SAVE_CONTACT_BUTTON",
    "BUSINESS_CARDS_ADVANCED_STATS",
    "TEAM_MEMBER_ACCESS_1",
    "USER_DATA_SPACES_EXTEND_10",
    "CUSTOM_REPORTS",
  ],
} as const;

export const PRICING_PAGE_VISIBILITY: Record<
  keyof typeof FEATURE_PERMISSIONS,
  boolean
> = {
  USER_MANAGEMENT: false,
  GROUP_MANAGEMENT: false,
  PROFILE_MANAGEMENT: false,

  USER_EVENTS: false,
  USER_NOTES: true,
  USER_DATA_SPACES: true,
  USER_DATA_SPACES_EXTEND_5: true,
  USER_DATA_SPACES_EXTEND_10: true,
  USER_DATA_SPACES_EXTEND_UNLIMITED: true,

  SEND_MESSAGES: true,
  BULK_MESSAGING: true,
  SEND_SMS: true,
  SEND_WHATSAPP: true,
  AI_QUALITY_CONTROL: true,
  PHONE_NUMBER_VALIDATION: true,

  LOYALTY_PROGRAM_ACCESS: true,
  LOYALTY_POINTS_BASIC: false,
  LOYALTY_PROGRAM_ALL_TYPE: true,
  AUTO_PILOT: true,
  GOOGLE_REVIEWS: true,
  GOOGLE_APPLE_WALLET: true,
  LOYALTY_CALLBOT: true,
  LOYALTY_GAMING: true,

  MANUAL_USER_VIA_FORMS: true,
  ADD_USER_MANUALLY: false,
  BULK_USER_IMPORT: true,

  SERVICE_AVATAR_UPDATE: true,
  PROFILE_CUSTOMIZATION: false,

  ENTITY_PREFERENCES: false,
  ENTITY_STATUS_MANAGEMENT: false,
  ROLE_TYPE_EDITOR: false,

  LOGIN_TEMPLATES_BASIC: false,
  CUSTOM_CALLBACK_URL: true,

  BUSINESS_CARDS_BASIC: true,
  BUSINESS_CARDS_REGISTER_FEATURE: true,
  BUSINESS_CARDS_SAVE_CONTACT_BUTTON: true,
  BUSINESS_CARDS_THEMES: true,
  BUSINESS_CARDS_UPLOAD_FILES: true,

  CUSTOM_LINKS: true,

  ADVANCED_SEARCH: true,
  AI_ENGINE: true,

  SIGNUP_FORM: true,
  SIGNUP_FORM_EXTENDED_LIMIT: true,
  SIGNUP_FORM_ENTERPRISE_LIMIT: true,
  SIGNUP_FORM_UNLIMITED: true,
  SIGNUP_FORM_REDIRECT_URL: false,
  CUSTOM_REGISTRATION_AUTOMATED_EMAIL: false,
  CUSTOM_REGISTRATION_EMAIL_ATTACHMENTS: false,
  KEEP_UNVERIFIED_USERS_POLICY: false,
  ROLE_MANAGEMENT_ACCESS: false,

  EXPORT_DATA: true,
  API_ACCESS: true,
  SWAGGER_DOCUMENTATION: false,
  WEBHOOK_EVENTS: true,
  SYSTEM_INTEGRATION: true,
  CUSTOM_DEVELOPMENT: true,

  ROI_GUARANTEE: true,
  SETUP_PACK: false,
  SUPPORT_ACCESS: false,
  BUSINESS_PARTNER_PANEL: true,
  DEDICATED_ACCOUNT_MANAGER: true,
  EXPERT_SUPPORT_12H: true,

  CUSTOM_REPORTS: true,
  RATE_LIMIT_PREMIUM: false,
  RATE_LIMIT_ENTERPRISE: false,
  RATE_LIMIT_LARGE_BUSINESS: false,
  USER_EVENT_ACCESS: false,
  BUSINESS_CARDS_STATS_VIEW: true,
  BUSINESS_CARDS_ADVANCED_STATS: true,

  PAYMENT_REQUEST_CREATION: true,

  FREE_EVENTS: true,
  PAID_EVENTS: true,

  MOBILE_APP_ACCESS: false,
  MOBILE_APP_TEAM_ACCESS_1: true,
  MOBILE_APP_TEAM_ACCESS_MULTI: true,

  TEAM_MEMBER_ACCESS_1: true,

  ESIGNATURE_ACCESS: true,

  IMPORT_FROM_DOCUMENT: true,

  LEADS_MANAGEMENT: true,

  TODOS_MANAGEMENT: true,

  AGENDA: true,
} as const;

export enum FeatureCategory {
  MESSAGING = "messaging",
  LOYALTY = "loyalty",
  BUSINESS_CARDS = "business_cards",
  INTEGRATION = "integration",
  SUPPORT = "support",
  USER_MANAGEMENT = "user_management",
  PAYMENT = "payment",
  EVENTS = "events",
  SIGNATURE = "signature",
  SEARCH = "search",
  SIGNUP_FORM = "signup_form",
  AI = "ai",
}

export const FEATURE_CATEGORIES: Record<
  keyof typeof FEATURE_PERMISSIONS,
  FeatureCategory
> = {
  USER_MANAGEMENT: FeatureCategory.USER_MANAGEMENT,
  GROUP_MANAGEMENT: FeatureCategory.USER_MANAGEMENT,
  PROFILE_MANAGEMENT: FeatureCategory.USER_MANAGEMENT,

  USER_EVENTS: FeatureCategory.USER_MANAGEMENT,
  USER_NOTES: FeatureCategory.USER_MANAGEMENT,
  USER_DATA_SPACES: FeatureCategory.USER_MANAGEMENT,
  USER_DATA_SPACES_EXTEND_5: FeatureCategory.USER_MANAGEMENT,
  USER_DATA_SPACES_EXTEND_10: FeatureCategory.USER_MANAGEMENT,
  USER_DATA_SPACES_EXTEND_UNLIMITED: FeatureCategory.USER_MANAGEMENT,

  SEND_MESSAGES: FeatureCategory.MESSAGING,
  BULK_MESSAGING: FeatureCategory.MESSAGING,
  SEND_SMS: FeatureCategory.MESSAGING,
  SEND_WHATSAPP: FeatureCategory.MESSAGING,
  AI_QUALITY_CONTROL: FeatureCategory.MESSAGING,
  PHONE_NUMBER_VALIDATION: FeatureCategory.USER_MANAGEMENT,

  LOYALTY_PROGRAM_ACCESS: FeatureCategory.LOYALTY,
  LOYALTY_POINTS_BASIC: FeatureCategory.LOYALTY,
  GOOGLE_REVIEWS: FeatureCategory.LOYALTY,
  GOOGLE_APPLE_WALLET: FeatureCategory.LOYALTY,
  LOYALTY_CALLBOT: FeatureCategory.AI,
  LOYALTY_PROGRAM_ALL_TYPE: FeatureCategory.LOYALTY,
  LOYALTY_GAMING: FeatureCategory.LOYALTY,
  AUTO_PILOT: FeatureCategory.AI,

  MANUAL_USER_VIA_FORMS: FeatureCategory.USER_MANAGEMENT,
  ADD_USER_MANUALLY: FeatureCategory.USER_MANAGEMENT,
  BULK_USER_IMPORT: FeatureCategory.USER_MANAGEMENT,

  SERVICE_AVATAR_UPDATE: FeatureCategory.USER_MANAGEMENT,
  PROFILE_CUSTOMIZATION: FeatureCategory.USER_MANAGEMENT,

  ENTITY_PREFERENCES: FeatureCategory.USER_MANAGEMENT,
  ENTITY_STATUS_MANAGEMENT: FeatureCategory.USER_MANAGEMENT,
  ROLE_TYPE_EDITOR: FeatureCategory.USER_MANAGEMENT,

  LOGIN_TEMPLATES_BASIC: FeatureCategory.USER_MANAGEMENT,
  CUSTOM_CALLBACK_URL: FeatureCategory.SIGNUP_FORM,

  BUSINESS_CARDS_BASIC: FeatureCategory.BUSINESS_CARDS,
  BUSINESS_CARDS_REGISTER_FEATURE: FeatureCategory.BUSINESS_CARDS,
  BUSINESS_CARDS_SAVE_CONTACT_BUTTON: FeatureCategory.BUSINESS_CARDS,
  BUSINESS_CARDS_THEMES: FeatureCategory.BUSINESS_CARDS,
  BUSINESS_CARDS_UPLOAD_FILES: FeatureCategory.BUSINESS_CARDS,

  CUSTOM_LINKS: FeatureCategory.BUSINESS_CARDS,

  ADVANCED_SEARCH: FeatureCategory.SEARCH,
  AI_ENGINE: FeatureCategory.AI,

  SIGNUP_FORM: FeatureCategory.SIGNUP_FORM,
  SIGNUP_FORM_EXTENDED_LIMIT: FeatureCategory.SIGNUP_FORM,
  SIGNUP_FORM_ENTERPRISE_LIMIT: FeatureCategory.SIGNUP_FORM,
  SIGNUP_FORM_UNLIMITED: FeatureCategory.SIGNUP_FORM,
  SIGNUP_FORM_REDIRECT_URL: FeatureCategory.SIGNUP_FORM,
  CUSTOM_REGISTRATION_AUTOMATED_EMAIL: FeatureCategory.SIGNUP_FORM,
  CUSTOM_REGISTRATION_EMAIL_ATTACHMENTS: FeatureCategory.SIGNUP_FORM,
  KEEP_UNVERIFIED_USERS_POLICY: FeatureCategory.SIGNUP_FORM,
  ROLE_MANAGEMENT_ACCESS: FeatureCategory.USER_MANAGEMENT,

  EXPORT_DATA: FeatureCategory.INTEGRATION,
  API_ACCESS: FeatureCategory.INTEGRATION,
  SWAGGER_DOCUMENTATION: FeatureCategory.INTEGRATION,
  WEBHOOK_EVENTS: FeatureCategory.INTEGRATION,
  SYSTEM_INTEGRATION: FeatureCategory.INTEGRATION,
  CUSTOM_DEVELOPMENT: FeatureCategory.INTEGRATION,

  ROI_GUARANTEE: FeatureCategory.SUPPORT,
  SETUP_PACK: FeatureCategory.SUPPORT,
  BUSINESS_PARTNER_PANEL: FeatureCategory.SUPPORT,
  SUPPORT_ACCESS: FeatureCategory.SUPPORT,
  DEDICATED_ACCOUNT_MANAGER: FeatureCategory.SUPPORT,
  EXPERT_SUPPORT_12H: FeatureCategory.SUPPORT,

  CUSTOM_REPORTS: FeatureCategory.USER_MANAGEMENT,
  RATE_LIMIT_PREMIUM: FeatureCategory.INTEGRATION,
  RATE_LIMIT_ENTERPRISE: FeatureCategory.INTEGRATION,
  RATE_LIMIT_LARGE_BUSINESS: FeatureCategory.INTEGRATION,
  USER_EVENT_ACCESS: FeatureCategory.USER_MANAGEMENT,
  BUSINESS_CARDS_STATS_VIEW: FeatureCategory.BUSINESS_CARDS,
  BUSINESS_CARDS_ADVANCED_STATS: FeatureCategory.BUSINESS_CARDS,

  PAYMENT_REQUEST_CREATION: FeatureCategory.PAYMENT,

  FREE_EVENTS: FeatureCategory.EVENTS,
  PAID_EVENTS: FeatureCategory.EVENTS,

  MOBILE_APP_ACCESS: FeatureCategory.LOYALTY,
  MOBILE_APP_TEAM_ACCESS_1: FeatureCategory.LOYALTY,
  MOBILE_APP_TEAM_ACCESS_MULTI: FeatureCategory.LOYALTY,

  TEAM_MEMBER_ACCESS_1: FeatureCategory.USER_MANAGEMENT,

  ESIGNATURE_ACCESS: FeatureCategory.SIGNATURE,

  IMPORT_FROM_DOCUMENT: FeatureCategory.USER_MANAGEMENT,

  LEADS_MANAGEMENT: FeatureCategory.USER_MANAGEMENT,

  TODOS_MANAGEMENT: FeatureCategory.USER_MANAGEMENT,

  AGENDA: FeatureCategory.USER_MANAGEMENT,
};

export const USER_LIMITS = {
  [PlanTier.FREE]: 25,
  [PlanTier.PREMIUM]: 1000,
  [PlanTier.ENTERPRISE]: 5000,
  [PlanTier.LARGE_BUSINESS]: Number.MAX_SAFE_INTEGER,
  [PlanTier.BUSINESS_PARTNER]: 25,
  [PlanTier.PROMO_ENTERPRISE_TRIAL]: 5000,
} as const;

export const RATE_LIMITS = {
  [PlanTier.FREE]: 100,
  [PlanTier.PREMIUM]: 1500,
  [PlanTier.ENTERPRISE]: 10000,
  [PlanTier.LARGE_BUSINESS]: 500000,
  [PlanTier.BUSINESS_PARTNER]: 100,
  [PlanTier.PROMO_ENTERPRISE_TRIAL]: 10000,
} as const;

export const LEAD_LIMITS = {
  [PlanTier.FREE]: 50,
  [PlanTier.PREMIUM]: 100,
  [PlanTier.ENTERPRISE]: 100,
  [PlanTier.LARGE_BUSINESS]: 100,
  [PlanTier.BUSINESS_PARTNER]: 50,
  [PlanTier.PROMO_ENTERPRISE_TRIAL]: 100,
} as const;

export const TODO_BOARD_LIMITS = {
  [PlanTier.FREE]: 1,
  [PlanTier.PREMIUM]: 3,
  [PlanTier.ENTERPRISE]: 10,
  [PlanTier.LARGE_BUSINESS]: 50,
  [PlanTier.BUSINESS_PARTNER]: 1,
  [PlanTier.PROMO_ENTERPRISE_TRIAL]: 10,
} as const;

export const TODO_TASK_LIMITS = {
  [PlanTier.FREE]: 50,
  [PlanTier.PREMIUM]: 50,
  [PlanTier.ENTERPRISE]: 50,
  [PlanTier.LARGE_BUSINESS]: 50,
  [PlanTier.BUSINESS_PARTNER]: 50,
  [PlanTier.PROMO_ENTERPRISE_TRIAL]: 50,
} as const;

export class PermissionManager {
  protected user: IUser;

  constructor(user: IUser) {
    this.user = user;
  }

  getPlanTier(): PlanTier | null {
    const topPlan = this.user.customer_top_plan;
    if (!topPlan) return null;

    for (const [tier, plans] of Object.entries(PLAN_MAPPINGS)) {
      if ((plans as readonly string[]).includes(topPlan)) {
        return tier as PlanTier;
      }
    }
    return null;
  }

  isPromoTrial(): boolean {
    return (
      this.getPlanTier() === PlanTier.PROMO_ENTERPRISE_TRIAL ||
      this.user.is_promo_trial === true
    );
  }

  isFeatureRestrictedForPromoTrial(feature: string): boolean {
    const restrictions = this.user.trial_restrictions;
    if (!restrictions) {
      return false;
    }

    if (
      (feature === "SEND_MESSAGES" || feature === "BULK_MESSAGING") &&
      restrictions.messaging_disabled
    ) {
      return true;
    }
    if (feature === "SEND_SMS" && restrictions.sms_disabled) {
      return true;
    }
    if (feature === "SEND_WHATSAPP" && restrictions.whatsapp_disabled) {
      return true;
    }
    if (feature === "API_ACCESS" && restrictions.api_disabled) {
      return true;
    }
    if (
      (feature === "AI_ENGINE" || feature === "IMPORT_FROM_DOCUMENT") &&
      restrictions.ai_disabled
    ) {
      return true;
    }
    if (feature === "AUTO_PILOT" && restrictions.auto_pilot_disabled) {
      return true;
    }

    return false;
  }

  hasFeatureAccess(feature: keyof typeof FEATURE_PERMISSIONS): boolean {
    const planTier = this.getPlanTier();
    if (!planTier) return false;

    // Partner (granted) plans map to the Enterprise tier but must not get WhatsApp.
    if (
      feature === "SEND_WHATSAPP" &&
      PARTNER_PLAN_CODES.includes(this.user.customer_top_plan || "")
    ) {
      return false;
    }

    if (
      planTier === PlanTier.PROMO_ENTERPRISE_TRIAL ||
      this.user.is_promo_trial
    ) {
      if (this.isFeatureRestrictedForPromoTrial(feature)) {
        return false;
      }
      const allowedTiers = FEATURE_PERMISSIONS[feature] as readonly PlanTier[];
      return (
        allowedTiers.includes(PlanTier.ENTERPRISE) ||
        allowedTiers.includes(planTier)
      );
    }

    const allowedTiers = FEATURE_PERMISSIONS[feature] as readonly PlanTier[];
    return allowedTiers.includes(planTier);
  }

  isOwner(): boolean {
    return (
      this.user.user_role === "OWNER" ||
      this.user.userRole === "OWNER" ||
      this.user.is_owner === true
    );
  }

  isRoleGoverned(): boolean {
    if (this.isOwner()) return false;

    const rp = this.user.role_permissions;
    if (rp !== null && rp !== undefined && typeof rp === "object") return true;

    const role = (
      this.user.user_role ||
      this.user.userRole ||
      ""
    ).toUpperCase();
    return role !== "" && !UNGOVERNED_ROLES.has(role);
  }

  can(capabilityKey: string, level: RbacLevel = "view"): boolean {
    if (!(CAPABILITIES as Record<string, unknown>)[capabilityKey]) return false;
    if (!isGrantLevel(level)) return false;

    if (!this.isRoleGoverned()) return true;

    const grantedLevel = (this.user.role_permissions || {})[capabilityKey];
    if (typeof grantedLevel !== "string") return false;

    return levelRank(grantedLevel) >= levelRank(level);
  }

  isPlanTier(tier: PlanTier): boolean {
    const userTier = this.getPlanTier();
    return (
      userTier === tier ||
      (tier === PlanTier.FREE && userTier === PlanTier.BUSINESS_PARTNER)
    );
  }

  isAnyPlanTier(tiers: PlanTier[]): boolean {
    const userTier = this.getPlanTier();
    return userTier ? tiers.includes(userTier) : false;
  }

  isAtLeastTier(minimumTier: PlanTier): boolean {
    const tierHierarchy = [
      PlanTier.FREE,
      PlanTier.PREMIUM,
      PlanTier.ENTERPRISE,
      PlanTier.LARGE_BUSINESS,
    ];

    let userTier = this.getPlanTier();
    if (!userTier) return false;
    if (minimumTier === PlanTier.BUSINESS_PARTNER) {
      return userTier === PlanTier.BUSINESS_PARTNER;
    }

    if (userTier === PlanTier.PROMO_ENTERPRISE_TRIAL) {
      userTier = PlanTier.ENTERPRISE;
    }
    if (userTier === PlanTier.BUSINESS_PARTNER) {
      userTier = PlanTier.FREE;
    }

    const userTierIndex = tierHierarchy.indexOf(userTier);
    const minimumTierIndex = tierHierarchy.indexOf(minimumTier);

    return userTierIndex >= minimumTierIndex;
  }

  canManageRoles(): boolean {
    return (
      this.hasFeatureAccess("ROLE_MANAGEMENT_ACCESS") && !this.isPromoTrial()
    );
  }

  getUserLimit(): number {
    const planTier = this.getPlanTier();
    return planTier ? USER_LIMITS[planTier] : 0;
  }

  hasValidSubscription(): boolean {
    return Boolean(
      this.user.hasValidSubscription &&
        this.user.customer_plans &&
        this.user.customer_plans.length > 0
    );
  }

  hasSubscriptionEnded(): boolean {
    return !this.hasValidSubscription();
  }

  getPlanDisplayName(): string {
    const tier = this.getPlanTier();
    if (!tier) return "No Plan";

    const displayNames: Record<PlanTier, string> = {
      [PlanTier.FREE]: "Free Forever",
      [PlanTier.PREMIUM]: "Small Business",
      [PlanTier.LARGE_BUSINESS]: "Growing Business",
      [PlanTier.ENTERPRISE]: "Enterprise",
      [PlanTier.BUSINESS_PARTNER]: "Business Partner",
      [PlanTier.PROMO_ENTERPRISE_TRIAL]: "Enterprise Trial",
    };

    return displayNames[tier] || "Unknown Plan";
  }

  getPromoTrialInfo(): {
    daysLeft: number;
    endDate: string;
    restrictions: {
      messaging_disabled: boolean;
      sms_disabled: boolean;
      api_disabled: boolean;
    };
  } | null {
    if (!this.isPromoTrial()) return null;

    return {
      daysLeft: this.user.trial_left_days || 0,
      endDate: this.user.trial_end_date || "",
      restrictions: this.user.trial_restrictions || {
        messaging_disabled: true,
        sms_disabled: true,
        api_disabled: true,
      },
    };
  }

  requiresUpgrade(feature: keyof typeof FEATURE_PERMISSIONS): boolean {
    return !this.hasFeatureAccess(feature);
  }

  getUpgradeTierForFeature(
    feature: keyof typeof FEATURE_PERMISSIONS
  ): PlanTier | null {
    const allowedTiers = FEATURE_PERMISSIONS[feature] as readonly PlanTier[];
    const currentTier = this.getPlanTier();

    const tierHierarchy = [
      PlanTier.FREE,
      PlanTier.PREMIUM,
      PlanTier.LARGE_BUSINESS,
      PlanTier.ENTERPRISE,
    ];
    const currentIndex = currentTier ? tierHierarchy.indexOf(currentTier) : -1;

    for (let i = currentIndex + 1; i < tierHierarchy.length; i++) {
      const tier = tierHierarchy[i];
      if (allowedTiers.includes(tier)) {
        return tier;
      }
    }

    return null;
  }

  isRootUser(): boolean {
    return Boolean(this.user.is_root);
  }
}

export const usePermissions = (user: IUser) => {
  const borrowsOwnerPlan = user.borrows_owner_plan === true;

  const hasOwnerPlanData = !!(
    user.owner_top_plan ||
    (user.owner_plans && user.owner_plans.length > 0)
  );

  const effectiveUser = useMemo(() => {
    if (borrowsOwnerPlan && hasOwnerPlanData) {
      return {
        ...user,
        customer_top_plan: user.owner_top_plan || "",
        customer_plans: user.owner_plans || [],
        hasValidSubscription: (user.owner_plans || []).length > 0,
      };
    }
    return user;
  }, [user, borrowsOwnerPlan, hasOwnerPlanData]);

  const permissionManager = useMemo(
    () => new PermissionManager(effectiveUser),
    [effectiveUser]
  );

  return useMemo(
    () => ({
      hasFeatureAccess: (feature: keyof typeof FEATURE_PERMISSIONS) =>
        permissionManager.hasFeatureAccess(feature),
      can: (capability: CapabilityKey, level: RbacLevel = "view") =>
        permissionManager.can(capability, level),
      isRoleGoverned: () => permissionManager.isRoleGoverned(),
      isOwner: () => permissionManager.isOwner(),
      isPlanTier: (tier: PlanTier) => permissionManager.isPlanTier(tier),
      isAnyPlanTier: (tiers: PlanTier[]) =>
        permissionManager.isAnyPlanTier(tiers),
      isAtLeastTier: (tier: PlanTier) => permissionManager.isAtLeastTier(tier),
      isPromoTrial: () => permissionManager.isPromoTrial(),
      canManageRoles: () => permissionManager.canManageRoles(),
      getUserLimit: () => permissionManager.getUserLimit(),
      hasValidSubscription: () => permissionManager.hasValidSubscription(),
      hasSubscriptionEnded: () => permissionManager.hasSubscriptionEnded(),
      getPlanDisplayName: () => permissionManager.getPlanDisplayName(),
      getPlanTier: () => permissionManager.getPlanTier(),
      requiresUpgrade: (feature: keyof typeof FEATURE_PERMISSIONS) =>
        permissionManager.requiresUpgrade(feature),
      getUpgradeTierForFeature: (feature: keyof typeof FEATURE_PERMISSIONS) =>
        permissionManager.getUpgradeTierForFeature(feature),
      isRootUser: () => permissionManager.isRootUser(),

      borrowsOwnerPlan,
      isUsingOwnerPlan: borrowsOwnerPlan && hasOwnerPlanData,
      hasOwnerPlanData,

      ownerPlanInfo: borrowsOwnerPlan
        ? {
            top_plan: user.owner_top_plan,
            plans: user.owner_plans,
          }
        : null,

      _manager: permissionManager,
    }),
    [permissionManager, borrowsOwnerPlan, hasOwnerPlanData, user]
  );
};

/**
 * Legacy plan tier checking functions - these should be migrated to use PermissionManager
 * @deprecated Use PermissionManager.isPlanTier() instead
 */

/** @deprecated Use PermissionManager.isPlanTier(PlanTier.FREE) instead */
export const isFreePlan = (planName: string | undefined): boolean => {
  return planName
    ? PLAN_MAPPINGS[PlanTier.FREE].includes(planName as any)
    : false;
};

export const isValidUser = (user: any): user is IUser => {
  return Boolean(
    user && typeof user === "object" && "customer_top_plan" in user
  );
};

export interface PlanFeatureInfo {
  feature: keyof typeof FEATURE_PERMISSIONS;
  displayName: string;
  isHighlight: boolean;
  showInPricingPage: boolean;
}

export const getPlanFeatures = (planTier: PlanTier): PlanFeatureInfo[] => {
  const allFeatures = Object.keys(FEATURE_PERMISSIONS) as Array<
    keyof typeof FEATURE_PERMISSIONS
  >;
  const highlightFeatures = PLAN_HIGHLIGHT_FEATURES[planTier] || [];

  return allFeatures
    .filter((feature) => {
      const allowedTiers = FEATURE_PERMISSIONS[feature] as readonly PlanTier[];
      return allowedTiers.includes(planTier);
    })
    .map((feature) => ({
      feature,
      displayName: FEATURE_DISPLAY_NAMES[feature] || feature,
      isHighlight: (highlightFeatures as readonly string[]).includes(
        feature as string
      ),
      showInPricingPage: PRICING_PAGE_VISIBILITY[feature],
    }))
    .sort((a, b) => {
      if (a.isHighlight && !b.isHighlight) return -1;
      if (!a.isHighlight && b.isHighlight) return 1;
      return a.displayName.localeCompare(b.displayName);
    });
};

export const getPlanHighlights = (planTier: PlanTier): PlanFeatureInfo[] => {
  const highlights = PLAN_HIGHLIGHT_FEATURES[planTier] || [];

  return highlights.map((feature) => ({
    feature: feature as keyof typeof FEATURE_PERMISSIONS,
    displayName:
      FEATURE_DISPLAY_NAMES[feature as keyof typeof FEATURE_DISPLAY_NAMES] ||
      feature,
    isHighlight: true,
    showInPricingPage:
      PRICING_PAGE_VISIBILITY[feature as keyof typeof FEATURE_PERMISSIONS],
  }));
};

export const comparePlanFeatures = (fromTier: PlanTier, toTier: PlanTier) => {
  const fromFeatures = new Set(getPlanFeatures(fromTier).map((f) => f.feature));
  const toFeatures = new Set(getPlanFeatures(toTier).map((f) => f.feature));

  const newFeatures = Array.from(toFeatures).filter(
    (feature) => !fromFeatures.has(feature)
  );

  return {
    fromTier,
    toTier,
    currentFeatureCount: fromFeatures.size,
    newFeatureCount: toFeatures.size,
    additionalFeatures: newFeatures.map((feature) => ({
      feature,
      displayName: FEATURE_DISPLAY_NAMES[feature] || feature,
      isHighlight: (
        (PLAN_HIGHLIGHT_FEATURES[toTier] || []) as readonly string[]
      ).includes(feature as string),
      showInPricingPage: PRICING_PAGE_VISIBILITY[feature],
    })),
  };
};

export const getDifferentialPlanFeatures = (
  planTier: PlanTier
): PlanFeatureInfo[] => {
  const tierHierarchy = [
    PlanTier.FREE,
    PlanTier.PREMIUM,
    PlanTier.ENTERPRISE,
    PlanTier.LARGE_BUSINESS,
  ];
  const currentTierIndex = tierHierarchy.indexOf(planTier);

  const currentFeatures = new Set(
    getPlanFeatures(planTier).map((f) => f.feature)
  );

  const lowerTierFeatures = new Set<keyof typeof FEATURE_PERMISSIONS>();
  for (let i = 0; i < currentTierIndex; i++) {
    getPlanFeatures(tierHierarchy[i]).forEach((f) =>
      lowerTierFeatures.add(f.feature)
    );
  }

  const differentialFeatures = Array.from(currentFeatures).filter(
    (feature) => !lowerTierFeatures.has(feature)
  );

  const highlightFeatures = PLAN_HIGHLIGHT_FEATURES[planTier] || [];

  return differentialFeatures
    .map((feature) => ({
      feature,
      displayName: FEATURE_DISPLAY_NAMES[feature] || feature,
      isHighlight: (highlightFeatures as readonly string[]).includes(
        feature as string
      ),
      showInPricingPage: PRICING_PAGE_VISIBILITY[feature],
    }))
    .sort((a, b) => {
      const indexA = (highlightFeatures as readonly string[]).indexOf(
        a.feature as string
      );
      const indexB = (highlightFeatures as readonly string[]).indexOf(
        b.feature as string
      );

      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      if (indexA !== -1) return -1;

      if (indexB !== -1) return 1;

      return a.displayName.localeCompare(b.displayName);
    });
};

export const getFeatureDisplayName = (
  feature: keyof typeof FEATURE_PERMISSIONS
): string => {
  return (
    FEATURE_DISPLAY_NAMES[feature] ||
    feature.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  );
};

export interface PermissionErrorInfo {
  title: string;
  message: string;
  feature: string;
  currentPlan: string;
  recommendedPlan: string | null;
  actionText: string;
  translationKey: string;
}

export const generatePermissionError = (
  feature: keyof typeof FEATURE_PERMISSIONS,
  user: IUser,
  customMessage?: string
): PermissionErrorInfo => {
  const pm = new PermissionManager(user);
  const recommendedTier = pm.getUpgradeTierForFeature(feature);
  const featureDisplayName = getFeatureDisplayName(feature);

  return {
    title: "Premium Feature",
    message:
      customMessage || `${featureDisplayName} requires a premium subscription`,
    feature: featureDisplayName,
    currentPlan: pm.getPlanDisplayName(),
    recommendedPlan: recommendedTier
      ? PLAN_MAPPINGS[recommendedTier]
        ? recommendedTier
            .replace("_", " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())
        : "Premium"
      : "Premium",
    actionText: recommendedTier ? "Upgrade Plan" : "Contact Support",
    translationKey: "permissions.featureRestricted",
  };
};

export const generateSubscriptionError = (
  user: IUser,
  customMessage?: string
): PermissionErrorInfo => {
  const pm = new PermissionManager(user);

  return {
    title: "Subscription Required",
    message: customMessage || "Your subscription has expired or is not active",
    feature: "Active Subscription",
    currentPlan: pm.getPlanDisplayName(),
    recommendedPlan: "Premium",
    actionText: "Renew Subscription",
    translationKey: "permissions.subscriptionExpired",
  };
};

export const createPermissionError = (
  feature: keyof typeof FEATURE_PERMISSIONS,
  user: IUser,
  customMessage?: string
): PermissionError => {
  const errorInfo = generatePermissionError(feature, user, customMessage);
  const pm = new PermissionManager(user);

  return new PermissionError(
    errorInfo.message,
    feature,
    pm.getPlanTier() || "unknown",
    pm.getUpgradeTierForFeature(feature) || "premium"
  );
};

export const createSubscriptionError = (
  user: IUser,
  customMessage?: string
): SubscriptionError => {
  const errorInfo = generateSubscriptionError(user, customMessage);
  const pm = new PermissionManager(user);

  return new SubscriptionError(
    errorInfo.message,
    pm.hasValidSubscription() ? "active" : "inactive",
    "unknown",
    pm.getPlanTier() || "unknown"
  );
};

export const ERROR_MESSAGES = {
  FEATURE_RESTRICTED: (feature: string, plan: string) =>
    `${feature} is only available for ${plan} subscribers and above.`,

  SUBSCRIPTION_EXPIRED: () =>
    "Your subscription has expired. Please renew to continue using premium features.",

  USER_LIMIT_EXCEEDED: (currentCount: number, limit: number, plan: string) =>
    `You have reached your user limit (${currentCount}/${limit}). Upgrade to ${plan} for more users.`,

  FEATURE_UPGRADE_REQUIRED: (feature: string, plan: string) =>
    `To access ${feature}, please upgrade to ${plan} plan or higher.`,

  INVALID_SUBSCRIPTION: () =>
    "Your subscription is not valid or has been cancelled. Please contact support.",

  PERMISSION_DENIED: (feature: string) =>
    `You don't have permission to access ${feature}. Contact your administrator.`,
} as const;

export const getPermissionDebugInfo = (user: IUser) => {
  const pm = new PermissionManager(user);
  return {
    planTier: pm.getPlanTier(),
    planDisplayName: pm.getPlanDisplayName(),
    userLimit: pm.getUserLimit(),
    hasValidSubscription: pm.hasValidSubscription(),
    availableFeatures: Object.keys(FEATURE_PERMISSIONS).filter((feature) =>
      pm.hasFeatureAccess(feature as keyof typeof FEATURE_PERMISSIONS)
    ),
  };
};

export class PermissionCache {
  static get(key: string): boolean | undefined {
    const result = apiCache.get<boolean>(`permission_${key}`);
    return result === null ? undefined : result;
  }

  static set(key: string, value: boolean): void {
    apiCache.setWithMediumTTL(`permission_${key}`, value);
  }

  static clearAll(): void {
    apiCache.clear();
  }

  static generateKey(userPlan: string | undefined, operation: string): string {
    return `${userPlan || "none"}_${operation}`;
  }

  static getStats() {
    return apiCache.getStats();
  }
}

const memoizedGetPlanTier = (() => {
  let hitCount = 0;
  let missCount = 0;

  const fn = (customer_top_plan: string | undefined): PlanTier | null => {
    const key = `plan_tier_${customer_top_plan || "none"}`;

    const cached = apiCache.get<PlanTier | null>(key);
    if (cached !== undefined && cached !== null) {
      hitCount++;
      return cached;
    }
    if (cached === null) {
      hitCount++;
      return null;
    }

    missCount++;
    let result: PlanTier | null = null;

    if (customer_top_plan) {
      for (const [tier, plans] of Object.entries(PLAN_MAPPINGS)) {
        if ((plans as readonly string[]).includes(customer_top_plan)) {
          result = tier as PlanTier;
          break;
        }
      }
    }

    apiCache.setWithLongTTL(key, result);
    return result;
  };

  fn.getStats = () => ({
    hitCount,
    missCount,
    hitRate: hitCount / (hitCount + missCount) || 0,
    cacheSize: apiCache.getStats().totalEntries,
  });

  fn.clearCache = () => {
    apiCache.clear();
    hitCount = 0;
    missCount = 0;
  };

  return fn;
})();

class LazyPermissionChecker {
  private user: IUser;
  private essentialFeatures = new Set<keyof typeof FEATURE_PERMISSIONS>([
    "USER_MANAGEMENT",
    "PROFILE_MANAGEMENT",
  ]);

  constructor(user: IUser) {
    this.user = user;
  }

  isEssentialFeature(feature: keyof typeof FEATURE_PERMISSIONS): boolean {
    return this.essentialFeatures.has(feature);
  }

  hasFeatureAccess(feature: keyof typeof FEATURE_PERMISSIONS): boolean {
    const cacheKey = `lazy_feature_${this.user.customer_top_plan || "none"}_${
      this.user.is_promo_trial ? "trial" : "std"
    }_${feature}`;

    const cached = apiCache.get<boolean>(cacheKey);
    if (typeof cached === "boolean") {
      return cached;
    }

    const result = new PermissionManager(this.user).hasFeatureAccess(feature);

    apiCache.setWithMediumTTL(cacheKey, result);
    return result;
  }

  preloadEssentialFeatures(): void {
    this.essentialFeatures.forEach((feature) => {
      this.hasFeatureAccess(feature);
    });
  }

  clearCache(): void {
    apiCache.clear();
  }
}

export class OptimizedPermissionManager extends PermissionManager {
  private lazyChecker: LazyPermissionChecker;
  private planTier: PlanTier | null | undefined;

  constructor(user: IUser) {
    super(user);
    this.lazyChecker = new LazyPermissionChecker(user);
    this.lazyChecker.preloadEssentialFeatures();
  }

  getPlanTier(): PlanTier | null {
    if (this.planTier === undefined) {
      this.planTier = memoizedGetPlanTier(this.user.customer_top_plan);
    }
    return this.planTier;
  }

  hasFeatureAccess(feature: keyof typeof FEATURE_PERMISSIONS): boolean {
    const cacheKey = PermissionCache.generateKey(
      this.user.customer_top_plan,
      `feature_${feature}`
    );

    const cached = PermissionCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const result = this.lazyChecker.hasFeatureAccess(feature);

    PermissionCache.set(cacheKey, result);

    return result;
  }

  isPlanTier(tier: PlanTier): boolean {
    const cacheKey = PermissionCache.generateKey(
      this.user.customer_top_plan,
      `tier_${tier}`
    );

    const cached = PermissionCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const result = this.getPlanTier() === tier;

    PermissionCache.set(cacheKey, result);

    return result;
  }

  clearCache(): void {
    this.planTier = undefined;
    this.lazyChecker.clearCache();
  }

  getPerformanceStats() {
    return {
      planTierLookup: memoizedGetPlanTier.getStats(),
      cacheStats: PermissionCache.getStats(),
    };
  }
}

export const useOptimizedPermissions = (
  user: IUser,
  options: { enableCache?: boolean; preloadEssential?: boolean } = {}
) => {
  const { enableCache = true } = options;

  const permissionManager = useMemo(() => {
    if (enableCache) {
      return new OptimizedPermissionManager(user);
    }
    return new PermissionManager(user);
  }, [enableCache, user]);

  const cachedMethods = useMemo(
    () => ({
      hasFeatureAccess:
        permissionManager.hasFeatureAccess.bind(permissionManager),
      isPlanTier: permissionManager.isPlanTier.bind(permissionManager),
      isAnyPlanTier: permissionManager.isAnyPlanTier.bind(permissionManager),
      isAtLeastTier: permissionManager.isAtLeastTier.bind(permissionManager),
      getUserLimit: permissionManager.getUserLimit.bind(permissionManager),
      hasValidSubscription:
        permissionManager.hasValidSubscription.bind(permissionManager),
      requiresUpgrade:
        permissionManager.requiresUpgrade.bind(permissionManager),
      getPlanDisplayName:
        permissionManager.getPlanDisplayName.bind(permissionManager),
      getPlanTier: permissionManager.getPlanTier.bind(permissionManager),
    }),
    [permissionManager]
  );

  return {
    ...cachedMethods,
    _manager: permissionManager,
    _getStats:
      enableCache &&
      typeof (permissionManager as any).getPerformanceStats === "function"
        ? () =>
            (
              permissionManager as OptimizedPermissionManager
            ).getPerformanceStats()
        : undefined,
  };
};
